import React, { useEffect } from 'react';
import { createPortal } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import BookingForm from './BookingForm';

const BookingModal = ({ isOpen, onClose, event }) => {
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen || !event) return null;

    // Create a portal to render the modal at the document root
    // detailed-booking-modal-portal should be a div appended to body if not present
    let portalRoot = document.getElementById('nemtly-booking-modal-root');
    if (!portalRoot) {
        portalRoot = document.createElement('div');
        portalRoot.id = 'nemtly-booking-modal-root';
        document.body.appendChild(portalRoot);
    }

    return createPortal(
        <div className="nb-fixed nb-inset-0 nb-z-[9999] nb-flex nb-items-center nb-justify-center nb-p-4 sm:nb-p-6">
            {/* Backdrop */}
            <div
                className="nb-absolute nb-inset-0 nb-bg-gray-900/50 nb-backdrop-blur-sm nb-transition-opacity"
                onClick={onClose}
                aria-hidden="true"
            ></div>

            {/* Modal Panel */}
            <div
                className="nb-relative nb-w-full nb-max-w-2xl nb-max-h-[90vh] nb-overflow-y-auto nb-bg-white nb-rounded-2xl nb-shadow-2xl nb-transform nb-transition-all"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-headline"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="nb-absolute nb-top-4 nb-right-4 nb-p-2 nb-text-gray-400 hover:nb-text-gray-500 hover:nb-bg-gray-100 nb-rounded-full nb-transition-colors focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-indigo-500"
                    aria-label={__('Close modal', 'nemtly-booking')}
                >
                    <svg className="nb-w-6 nb-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="nb-p-6 sm:nb-p-10">
                    <div className="nb-text-center nb-mb-8">
                        <h3 className="nb-text-2xl nb-font-bold nb-text-gray-900" id="modal-headline">
                            {__('Book Your Spot', 'nemtly-booking')}
                        </h3>
                        {event.title && (
                            <p className="nb-mt-2 nb-text-gray-600">
                                {event.title?.rendered || event.title || `Event #${event.id}`}
                            </p>
                        )}
                    </div>

                    <BookingForm
                        eventId={event.id}
                        onSuccess={() => {
                            // Close modal after brief delay on success to show success message
                            setTimeout(onClose, 2000);
                        }}
                        className="nb-shadow-none nb-border-0 nb-p-0"
                    />
                </div>
            </div>
        </div>,
        portalRoot
    );
};

export default BookingModal;
